import {skipToken} from '@reduxjs/toolkit/query';
import {type Action, Card, DataStatus, type Hyperlink, showNotification} from 'platform/components';
import {Box, Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useGetFileByIdQuery} from '@dms/api/base';
import {
  pkwEnvkvApi,
  useCalculatePkwEnvkvMutation,
  useGeneratePkwEnvkvDocumentMutation,
  useGetPkwEnvkvStatusQuery,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError, useTenant} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useDidUpdate} from 'shared/web';

import {PkwEnvkvCardBody} from './components/PkwEnvkvCardBody';
import {derivePkwEnvkvState, type PkwEnvkvCardAction} from './utils/derivePkwEnvkvState';
import {getPkwEnvkvCalculationRows} from './utils/getPkwEnvkvCalculationRows';
import {getPollingInterval} from './utils/getPollingInterval';

export interface PkwEnvkvCardProps extends RequiredTestIdProps {
  vehicleId: string;
  canGenerateDocument: boolean;
}

function focusLinkedCard(element: HTMLDivElement | null) {
  if (isNotNil(element) && window.location.hash === `#${element.id}`) {
    element.scrollIntoView({block: 'center'});
    element.focus({preventScroll: true});
  }
}

export function PkwEnvkvCard(props: PkwEnvkvCardProps) {
  const {isTenantCountryRestricted} = useTenant();
  const isGermanTenant = isTenantCountryRestricted(['DEU']);
  const queryArg = isGermanTenant ? {vehicleId: props.vehicleId} : skipToken;
  const {currentData: cachedStatus} =
    pkwEnvkvApi.endpoints.getPkwEnvkvStatus.useQueryState(queryArg);
  const {
    data: status,
    isLoading,
    isFetching,
    isError,
    requestId,
  } = useGetPkwEnvkvStatusQuery(queryArg, {
    pollingInterval: getPollingInterval(cachedStatus?.workflowStatus),
    refetchOnFocus: true,
    // A remounted card may find a cached status from before the last change.
    refetchOnMountOrArgChange: true,
  });

  const [calculatePkwEnvkv, {isLoading: isCalculating}] = useCalculatePkwEnvkvMutation();
  const [generateDocument, {isLoading: isGenerating}] = useGeneratePkwEnvkvDocumentMutation();
  const formatCurrency = useFormatCurrency();

  // The generate endpoint returns 204 once the asynchronous generation is
  // accepted; the document exists only when a status fetched after that reaches
  // CURRENT with a file. The vehicle whose generation this user started is
  // remembered here and judged on every freshly completed status request, so
  // nothing is announced for a document that already existed, for another
  // vehicle or after a failure. The request identity matters: a retry can return
  // the very same failed payload, which keeps the cached status identity.
  const pendingGenerationVehicleIdRef = useRef<string | null>(null);
  const judgedStatusRequestIdRef = useRef<string | undefined>(undefined);

  useDidUpdate(() => {
    const pendingVehicleId = pendingGenerationVehicleIdRef.current;
    if (
      isNil(pendingVehicleId) ||
      isNil(status) ||
      isFetching ||
      isError ||
      requestId === judgedStatusRequestIdRef.current
    ) {
      return;
    }
    judgedStatusRequestIdRef.current = requestId;
    if (pendingVehicleId !== props.vehicleId) {
      pendingGenerationVehicleIdRef.current = null;
      return;
    }

    match(status.workflowStatus)
      .with('GENERATING', () => undefined)
      .with('CURRENT', () => {
        pendingGenerationVehicleIdRef.current = null;
        if (isNotNil(status.documentFileId)) {
          showNotification.success(i18n.t('page.pkwEnvkv.notifications.documentCreated'));
        }
      })
      .otherwise(() => {
        pendingGenerationVehicleIdRef.current = null;
      });
  }, [requestId, isFetching, isError, status, props.vehicleId]);

  const state = isNotNil(status)
    ? derivePkwEnvkvState(status, {canGenerateDocument: props.canGenerateDocument})
    : undefined;

  const previewFileId = state?.previewFileId ?? null;
  const {currentData: document} = useGetFileByIdQuery(
    isNil(previewFileId) ? skipToken : {fileId: previewFileId}
  );

  const calculationRows = getPkwEnvkvCalculationRows(status?.calculation, formatCurrency);

  const handleCalculate = () =>
    calculatePkwEnvkv({vehicleId: props.vehicleId})
      .unwrap()
      .then(() => undefined)
      .catch(handleApiError);
  const handleGenerateDocument = () =>
    generateDocument({vehicleId: props.vehicleId})
      .unwrap()
      .then(() => {
        // Only accepted so far: the status requests completed from now on decide
        // the outcome, never the status that was current when the request began.
        pendingGenerationVehicleIdRef.current = props.vehicleId;
        judgedStatusRequestIdRef.current = requestId;
      })
      .catch(handleApiError);

  const calculateAction: Action = {
    type: 'button',
    variant: 'link',
    leftIcon: 'action/autorenew',
    isDisabled: isGenerating,
    isLoading: isCalculating,
    onClick: handleCalculate,
    title: i18n.t('page.pkwEnvkv.actions.calculateTaxes'),
    'data-testid': suffixTestId('pkwEnvkvCard-calculate', props),
  };
  const generateDocumentAction: Action = {
    type: 'button',
    variant: 'link',
    leftIcon: 'action/description',
    isDisabled: isCalculating,
    isLoading: isGenerating,
    onClick: handleGenerateDocument,
    title: i18n.t('page.pkwEnvkv.actions.generateDocument'),
    'data-testid': suffixTestId('pkwEnvkvCard-generate', props),
  };

  const actions: Action[] | undefined =
    isNil(state) || state.actions.length === 0
      ? undefined
      : state.actions.map((action) =>
          match<PkwEnvkvCardAction, Action>(action)
            .with('calculate', () => calculateAction)
            .with('generate', () => generateDocumentAction)
            .exhaustive()
        );

  // A running attempt old enough for a backend retry: the same accepted-request
  // handling as the header actions, offered as an end link on the in-progress alert.
  const retryAction = state?.retryAction;
  const retryHyperlink: Hyperlink | undefined = isNil(retryAction)
    ? undefined
    : {
        leftIcon: 'action/autorenew',
        title: i18n.t('general.actions.retry'),
        href: '#',
        isDisabled: isCalculating || isGenerating,
        onClick: (event) => {
          event.preventDefault();
          const retry = match<PkwEnvkvCardAction, () => Promise<void>>(retryAction)
            .with('calculate', () => handleCalculate)
            .with('generate', () => handleGenerateDocument)
            .exhaustive();

          return retry();
        },
      };

  if (!isGermanTenant || state?.isVisible === false) {
    return null;
  }

  return (
    <Box id="pkw-envkv" tabIndex={-1} ref={focusLinkedCard}>
      <Card
        title={i18n.t('page.pkwEnvkv.title')}
        actions={actions}
        data-testid={suffixTestId('pkwEnvkvCard', props)}
      >
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          data-testid={suffixTestId('dataStatus', props)}
        >
          <Show when={isNotNil(state)}>
            <PkwEnvkvCardBody
              vehicleId={props.vehicleId}
              state={state}
              calculationRows={calculationRows}
              document={document}
              statusHyperlink={retryHyperlink}
              data-testid={props['data-testid']}
            />
          </Show>
        </DataStatus>
      </Card>
    </Box>
  );
}
